import { useCallback, useEffect, useState } from "react";
import { Package, UserCheck, CalendarDays, Loader2, Plus, X, Trash2, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const ASSET_TONE = { pendiente: "bg-gold/15 text-gold", aprobado: "bg-sky-500/15 text-sky-600", entregado: "bg-emerald-500/15 text-emerald-600", rechazado: "bg-destructive/15 text-destructive" };
const VISIT_TONE = { programado: "bg-gold/15 text-gold", en_sitio: "bg-emerald-500/15 text-emerald-600", finalizado: "bg-navy/10 text-navy/50", cancelado: "bg-destructive/15 text-destructive" };

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function EmployeeServices() {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("assets");
  useEffect(() => { api.get("/services/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  return (
    <OSLayout title="OMNIA Servicios al Empleado">
      <div className="space-y-6" data-testid="services-page">
        <div className="flex gap-2 border-b border-navy/10 pb-2">
          {[["assets", "Activos", Package], ["visitors", "Visitantes", UserCheck], ["bookings", "Reserva de espacios", CalendarDays]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`svc-tab-${k}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}><Ic className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "assets" && <Assets meta={meta} />}
            {tab === "visitors" && <Visitors meta={meta} />}
            {tab === "bookings" && <Bookings meta={meta} />}
          </>
        )}
      </div>
    </OSLayout>
  );
}

function Assets({ meta }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ asset_type: "", reason: "", urgency: "normal" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/services/assets?scope=${meta.is_leader ? "all" : "mine"}`).then((r) => setItems(r.data || [])).catch(() => {}); }, [meta.is_leader]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.asset_type.trim()) return toast.error("Indica el activo");
    setBusy(true);
    try { await api.post("/services/assets", f); toast.success("Solicitud enviada"); setOpen(false); setF({ asset_type: "", reason: "", urgency: "normal" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const setStatus = async (id, status) => { try { await api.post(`/services/assets/${id}/status`, { status }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/services/assets/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="svc-asset-new"><Plus className="w-4 h-4" /> Solicitar activo</button>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin solicitudes.</div>
        : items.map((a) => (
          <div key={a.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`svc-asset-${a.id}`}>
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{a.asset_type} <span className="text-xs text-navy/40">· {a.user_name}</span></div>{a.reason && <div className="text-xs text-navy/50">{a.reason}</div>}</div>
              <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded-full ${ASSET_TONE[a.status]}`}>{a.status}</span><button onClick={() => del(a.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
            {meta.is_leader && a.status !== "entregado" && a.status !== "rechazado" && (
              <div className="flex gap-2 mt-2">
                {a.status === "pendiente" && <button onClick={() => setStatus(a.id, "aprobado")} className="btn btn-ghost text-xs" data-testid={`svc-asset-approve-${a.id}`}>Aprobar</button>}
                {a.status === "aprobado" && <button onClick={() => setStatus(a.id, "entregado")} className="btn btn-primary text-xs">Marcar entregado</button>}
                <button onClick={() => setStatus(a.id, "rechazado")} className="btn btn-ghost text-xs text-destructive">Rechazar</button>
              </div>
            )}
          </div>
        ))}
      {open && (
        <Modal title="Solicitar activo" onClose={() => setOpen(false)} testid="svc-asset-dialog">
          <div className="space-y-3">
            <input className="field" placeholder="Activo (laptop, monitor, silla…)" value={f.asset_type} onChange={(e) => setF({ ...f, asset_type: e.target.value })} data-testid="svc-asset-type" />
            <textarea className="field" rows={2} placeholder="Motivo" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
            <select className="field" value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })}><option value="baja">Baja</option><option value="normal">Normal</option><option value="alta">Alta</option></select>
          </div>
          <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} disabled={busy} className="btn btn-primary" data-testid="svc-asset-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar"}</button></div>
        </Modal>
      )}
    </div>
  );
}

function Visitors({ meta }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ visitor_name: "", company: "", date: "", time: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/services/visitors?scope=${meta.is_leader ? "all" : "mine"}`).then((r) => setItems(r.data || [])).catch(() => {}); }, [meta.is_leader]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.visitor_name.trim() || !f.date) return toast.error("Nombre y fecha requeridos");
    setBusy(true);
    try { await api.post("/services/visitors", f); toast.success("Visita registrada"); setOpen(false); setF({ visitor_name: "", company: "", date: "", time: "", reason: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const act = async (id, action) => { try { await api.post(`/services/visitors/${id}/${action}`); load(); } catch { toast.error("Error"); } };
  const del = async (id) => { try { await api.delete(`/services/visitors/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="svc-visitor-new"><Plus className="w-4 h-4" /> Registrar visita</button>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin visitas.</div>
        : items.map((v) => (
          <div key={v.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`svc-visitor-${v.id}`}>
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{v.visitor_name} <span className="text-xs text-navy/40">{v.company}</span></div><div className="text-xs text-navy/50">{v.date} {v.time} · anfitrión: {v.host_name}</div></div>
              <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded-full ${VISIT_TONE[v.status]}`}>{v.status}</span><button onClick={() => del(v.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
            <div className="flex gap-2 mt-2">
              {v.status === "programado" && <button onClick={() => act(v.id, "checkin")} className="btn btn-ghost text-xs gap-1" data-testid={`svc-visitor-checkin-${v.id}`}><LogIn className="w-3.5 h-3.5" /> Check-in</button>}
              {v.status === "en_sitio" && <button onClick={() => act(v.id, "checkout")} className="btn btn-ghost text-xs gap-1" data-testid={`svc-visitor-checkout-${v.id}`}><LogOut className="w-3.5 h-3.5" /> Check-out</button>}
            </div>
          </div>
        ))}
      {open && (
        <Modal title="Registrar visita" onClose={() => setOpen(false)} testid="svc-visitor-dialog">
          <div className="space-y-3">
            <input className="field" placeholder="Nombre del visitante" value={f.visitor_name} onChange={(e) => setF({ ...f, visitor_name: e.target.value })} data-testid="svc-visitor-name" />
            <input className="field" placeholder="Empresa" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
            <div className="grid grid-cols-2 gap-3"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="svc-visitor-date" /><input type="time" className="field" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
            <textarea className="field" rows={2} placeholder="Motivo" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} disabled={busy} className="btn btn-primary" data-testid="svc-visitor-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}</button></div>
        </Modal>
      )}
    </div>
  );
}

function Bookings({ meta }) {
  const today = new Date().toLocaleDateString("sv-SE");
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(today);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ space: meta.spaces[0], date: today, start: "09:00", end: "10:00", note: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/services/bookings?date=${date}`).then((r) => setItems(r.data || [])).catch(() => {}); }, [date]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try { await api.post("/services/bookings", f); toast.success("Espacio reservado"); setOpen(false); load(); setDate(f.date); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const cancel = async (id) => { try { await api.delete(`/services/bookings/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <input type="date" className="field w-auto" value={date} onChange={(e) => setDate(e.target.value)} data-testid="svc-booking-date-filter" />
        <button onClick={() => { setF({ ...f, date }); setOpen(true); }} className="btn btn-primary gap-2" data-testid="svc-booking-new"><Plus className="w-4 h-4" /> Reservar espacio</button>
      </div>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin reservas ese día.</div>
        : <div className="grid gap-2 sm:grid-cols-2">{items.map((b) => (
            <div key={b.id} className="bg-white border border-navy/10 rounded-xl p-3 flex items-center justify-between" data-testid={`svc-booking-${b.id}`}>
              <div><div className="text-sm font-medium">{b.space}</div><div className="text-xs text-navy/50">{b.start}–{b.end} · {b.user_name}</div></div>
              <button onClick={() => cancel(b.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}</div>}
      {open && (
        <Modal title="Reservar espacio" onClose={() => setOpen(false)} testid="svc-booking-dialog">
          <div className="space-y-3">
            <select className="field" value={f.space} onChange={(e) => setF({ ...f, space: e.target.value })} data-testid="svc-booking-space">{meta.spaces.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="svc-booking-date" />
            <div className="grid grid-cols-2 gap-3"><input type="time" className="field" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="svc-booking-start" /><input type="time" className="field" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="svc-booking-end" /></div>
            <input className="field" placeholder="Nota (opcional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} disabled={busy} className="btn btn-primary" data-testid="svc-booking-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reservar"}</button></div>
        </Modal>
      )}
    </div>
  );
}
